import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { message, Popover, Spin, Tag, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { VrReminderTemplateVariable } from './voucherReminderUtils';
import { fetchVoucherReminderTemplateVariables } from './voucherReminderTemplateVars';

export interface VoucherReminderTemplateVariablesProps {
  /** inline: nút nhỏ + popover (mặc định). block: hiển thị đầy đủ (legacy) */
  variant?: 'inline' | 'block';
}

const VoucherReminderTemplateVariables = ({
  variant = 'inline',
}: VoucherReminderTemplateVariablesProps) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [variables, setVariables] = useState<VrReminderTemplateVariable[]>([]);
  const cmsLocale = intl.locale;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    fetchVoucherReminderTemplateVariables(cmsLocale)
      .then((list) => {
        if (!cancelled) setVariables(list);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, cmsLocale]);

  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      message.success(t('voucherReminder.templateVars.copied', { token }));
    } catch {
      message.info(token);
    }
  };

  const popoverContent = (
    <div style={{ width: 300 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('voucherReminder.templateVars.hint')}
      </Typography.Text>
      <Spin spinning={loading} size="small">
        <div style={{ marginTop: 8, minHeight: loading ? 48 : undefined }}>
          {variables.map((item) => (
            <div
              key={item.key}
              style={{
                display: 'grid',
                gridTemplateColumns: '108px 1fr',
                gap: 8,
                alignItems: 'start',
                marginBottom: 6,
                fontSize: 12,
                lineHeight: '18px',
              }}
            >
              <Tag
                color="blue"
                style={{
                  margin: 0,
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  fontSize: 11,
                  padding: '0 6px',
                  userSelect: 'all',
                }}
                onClick={() => copyToken(item.token)}
              >
                {item.token}
              </Tag>
              <span style={{ color: '#595959' }}>{item.description}</span>
            </div>
          ))}
        </div>
      </Spin>
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        {t('voucherReminder.templateVars.footer')}
      </Typography.Text>
    </div>
  );

  if (variant === 'block') {
    return (
      <div
        style={{
          marginBottom: 12,
          padding: '8px 10px',
          background: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: 4,
        }}
      >
        {popoverContent}
      </div>
    );
  }

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      placement="bottomRight"
      visible={open}
      onVisibleChange={setOpen}
      overlayClassName="vr-template-vars-popover"
    >
      <Typography.Link style={{ fontSize: 12 }}>
        <QuestionCircleOutlined style={{ marginRight: 4 }} />
        {t('voucherReminder.templateVars.title')}
      </Typography.Link>
    </Popover>
  );
};

export default VoucherReminderTemplateVariables;
